import { Injectable } from "@angular/core";
import { WebShopService } from "./webShop.service";
import { WebShopMode } from "./IWebShopService";
import { ServiceOpenTimeViewModel } from "../shared/models";
import { CoreLogService } from "./log.service";
import { BottomSheetHelperService } from "./bottomSheetHelper.service";
import { AvailableServiceTimeComponent, SetNewOrderTimeDialogComponent } from "../modalDialogs";
import { LocationService } from "../components/location/location.service";
import { IAvailableTimesConfig, INavigationOptions, ServiceTimeValidationResult, TimeSelectionStatus, TimeValidationResult } from "./IOrderTimeValidation.service";
import { Router } from "@angular/router";
import { NavigationRoute } from "../shared/enums/navigation-route.enum";

// todo Needs to be extended, all time checks should take place here (e.g. webshopService region time selection)
@Injectable({
  providedIn: "root"
})
export class OrderTimeValidationService {

  constructor(
    private _webshopService: WebShopService,
    private _locationService: LocationService,
    private _coreLogService: CoreLogService,
    private _router: Router,
    private _bottomSheetHelperService: BottomSheetHelperService
  ) { }


  private get serviceMode(): WebShopMode {
    return this._webshopService.getServiceMode();
  }

  private get locationId() {
    return this._webshopService.locationID;
  }

  private get selectedServiceOrderTime() {
    return this._webshopService.serviceOrderTime;
  }


  // region Time Validation

  /**
   * Validates if the selected service order time is still valid */
  private async validateSelectedServiceTime(): Promise<ServiceTimeValidationResult> {
    const me = this;

    //  Restore and check if time exists
    if (!me.selectedServiceOrderTime) {
      await me._webshopService.restoreAndSetServiceTimeFromCache();
    }
    // No time selected
    if (!me.selectedServiceOrderTime) {
      return ServiceTimeValidationResult.NoTimeSelected;
    }

    const selectedTime = me.selectedServiceOrderTime.clone();
    const now = me._webshopService.getMinimumValidServiceTime();

    // Future day is always valid (no need to check expiration)
    const isFutureDay = selectedTime.clone().startOf('day').isAfter(now.clone().startOf('day'));
    if (isFutureDay) {
      const isFullyBooked = await me.isSelectedTimeSlotFullyBooked();
      if (isFullyBooked) {
        return ServiceTimeValidationResult.SelectedTimeFullyBooked;
      }
      return ServiceTimeValidationResult.Valid;
    }

    // Today - check if today has any available slots
    const hasTodaySlots = await me._webshopService.hasValidTimesForToday();
    if (!hasTodaySlots) {
      return await me.checkIfAnyTimesAvailable();
    }

    // Check if selected time expired day & time
    const isPastDay = selectedTime.clone().startOf('day').isBefore(now.clone().startOf('day'));
    const isExpired = isPastDay || now.isAfter(selectedTime);
    if (isExpired) {
      return ServiceTimeValidationResult.SelectedTimeExpired;
    }

    // Time is not expired, but check if the selected time slot is fully booked
    const isFullyBooked = await me.isSelectedTimeSlotFullyBooked();
    if (isFullyBooked) {
      return ServiceTimeValidationResult.SelectedTimeFullyBooked; // ← Neuer Enum-Wert
    }

    // Selected time is valid
    return ServiceTimeValidationResult.Valid;
  }

  /**
   * Checks if any times are available on other days
   * @returns ClosedTodayAvailableLater if user can select another day,
   * CompletelyUnavailable if service is closed
   */
  private async checkIfAnyTimesAvailable(): Promise<ServiceTimeValidationResult> {
    const me = this;
    try {
      const openDays = await me._webshopService.getOpenDays();
      if (!openDays || openDays.length === 0) {
        return ServiceTimeValidationResult.CompletelyUnavailable;
      }

      // Search through all open days for available timeslots
      for (const day of openDays) {
        try {
          const availableTimes = await me._webshopService.getAvailableServiceTimes(day.value, day.use_time);
          if (availableTimes.length > 0) {
            return ServiceTimeValidationResult.ClosedTodayAvailableLater;
          }
        } catch (error) {
          me._coreLogService.error("TimeValidationService", "error checking day", day, error);
        }
      }
      // No valid times found on any day: service completely closed
      return ServiceTimeValidationResult.CompletelyUnavailable;
    } catch (error) {
      me._coreLogService.error("TimeValidationService", "Error checking availability", error);
      // On error: assume other days might be available to not block user
      return ServiceTimeValidationResult.ClosedTodayAvailableLater;
    }
  }

  /**
   * Validates current service time and handles user interaction if needed
   * @param navigateBeforeDialog Navigate to home BEFORE showing the dialog (service completely unavailable)
   * @param navigateOnClose Navigate AFTER dialog is closed
   * @returns Result indicating validation status and any time updates
   */
  public async validateServiceTime(navigationOptions: INavigationOptions): Promise<TimeValidationResult> {
    const me = this;
    const result = await me.validateSelectedServiceTime();

    // Selected time is still valid, no action needed
    if (result === ServiceTimeValidationResult.Valid) {
      return { status: TimeSelectionStatus.Valid };
    }

    // No time selected - caller handles navigation
    if (result === ServiceTimeValidationResult.NoTimeSelected) {
      return { status: TimeSelectionStatus.None };
    }

    // Service completely unavailable - navigate and show "closed" dialog
    if (result === ServiceTimeValidationResult.CompletelyUnavailable) {
      await me.showServiceUnavailableDialog(navigationOptions);
      return { status: TimeSelectionStatus.NoTimeToSelect };
    }

    // Service closed today but available later - show info dialog, user must go to OrderTimeSelector
    if (result === ServiceTimeValidationResult.ClosedTodayAvailableLater) {
      await me.showServiceClosedTodayDialog(navigationOptions);
      return { status: TimeSelectionStatus.NoTimeToSelect };
    }

    // Time is fully booked - show time selection dialog
    if (result === ServiceTimeValidationResult.SelectedTimeFullyBooked) {
      return await me.handleNewTimeSelection(false);
    }

    // Time expired but today has available slots - show time selection dialog with today's times
    return await me.handleNewTimeSelection(false);
  }


  /**
   * Handles time selection dialog and updates
   * @param isServiceClosedToday true if service closed today (only future days available), false if time just expired
   */
  public async handleNewTimeSelection(isServiceClosedToday: boolean): Promise<TimeValidationResult> {
    const me = this;
    const newTime = await me.openSetNewOrderTimeDialog(isServiceClosedToday);

    if (!newTime) {
      return { status: TimeSelectionStatus.Cancelled };
    }

    await me.applyNewServiceTime(newTime);
    return { status: TimeSelectionStatus.TimeUpdated, time: newTime };
  }


  /**
   * Checks if the selected time slot is fully booked
   * @returns True if fully booked, false if still available
   */
  private async isSelectedTimeSlotFullyBooked(): Promise<boolean> {
    const me = this;

    if (!me.selectedServiceOrderTime) {
      return false;
    }

    return await me._webshopService.isSelectedTimeSlotFullyBooked();
  }

  /**
   * displays service unavailable dialog (completely closed) and handles navigation
   */
  private async showServiceUnavailableDialog(navigationOptions: INavigationOptions): Promise<void> {
    const me = this;
    const location = await me._locationService.GetWebshopLocationInfoViewModel(me.serviceMode, me.locationId);
    const config: IAvailableTimesConfig = {
      serviceMode: me.serviceMode,
      location: location,
      navigationRoute: NavigationRoute.Home,
      hasValidOrderTimes: false,
      hasValidOrderTimesToday: false,
      showOnlyToday: false,
    }
    if (navigationOptions.navigateBeforeDialog) {
      // Navigate to home first then open dialog
      const navigationSuccess = await me._router.navigate([NavigationRoute.Home], { replaceUrl: true });
      if (navigationSuccess) {
        me.openAvailableServiceTime(config, navigationOptions.navigateOnClose);
        me._webshopService.setServiceMode(null);
      }
    } else {
      //open dialog and stay on current page then navigate on close
      me.openAvailableServiceTime(config, navigationOptions.navigateOnClose);
    }
  }

  /**
   * displays service closed today dialog (but available on future days) and navigates to OrderTimeSelector
   */
  private async showServiceClosedTodayDialog(navigationOptions: INavigationOptions): Promise<void> {
    const me = this;
    const location = await me._locationService.GetWebshopLocationInfoViewModel(me.serviceMode, me.locationId);
    const config: IAvailableTimesConfig = {
      serviceMode: me.serviceMode,
      location: location,
      navigationRoute: NavigationRoute.OrderTimeSelector,
      hasValidOrderTimes: true,
      hasValidOrderTimesToday: false,
      showOnlyToday: true,
    }
    if (navigationOptions.navigateBeforeDialog) {
      // Navigate to OrderTimeSelector first then open dialog
      const navigationSuccess = await me._router.navigate([NavigationRoute.OrderTimeSelector], { replaceUrl: true });
      if (navigationSuccess) {
        me.openAvailableServiceTime(config, navigationOptions.navigateOnClose);
      }
    } else {
      //open dialog and stay on current page, then navigate on close
      me.openAvailableServiceTime(config, navigationOptions.navigateOnClose);
    }
  }

  // endregion

  // region Helper Methods

  /**
   * Applies the new service time selected by the user
   * Sets ASAP flag, service order time and caches the selection
   */
  private async applyNewServiceTime(newTime: ServiceOpenTimeViewModel): Promise<void> {
    const me = this;
    me._webshopService.orderASAP = newTime.asap;
    me._webshopService.setServiceOrderTime(newTime.value);
    await me._webshopService.setCachedServiceTime(newTime.value);
  }

  private loadNavigtion(result: boolean, data: IAvailableTimesConfig) {
    const me = this;
    if (result) {
      const route = data.navigationRoute ? data.navigationRoute : NavigationRoute.Home;
      me._router.navigate([route]);
      // User closed dialog via X button
    } else {
      me._router.navigate([NavigationRoute.Home]);

    }
  }

  // endregion

  // region Dialogs
  /**
   * Opens dialog for selecting a new order time
   * @param isClosed true if service closed (no times today), false if only current time expired
   * @returns Selected time from dialog or null if user cancelled
   */
  private openSetNewOrderTimeDialog(isClosed: boolean): Promise<ServiceOpenTimeViewModel | null> {
    const me = this;
    const selectTimeDialog = me._bottomSheetHelperService.open(SetNewOrderTimeDialogComponent, {
      data: isClosed,
      panelClass: "mat-bottom-sheet-container--full-height",
    });
    return selectTimeDialog.afterDismissed().toPromise();
  }

  /**
 * Opens info dialog displaying available service times.
 *
 * @param data - Configuration object
 * @param navigateOnClose - Navigate after dialog is closed
 */
  public openAvailableServiceTime(data: IAvailableTimesConfig, navigateOnClose: boolean) {
    const me = this;

    // Determine whether to show only today or all days
    const isLocationOpen = data.showOnlyToday !== undefined ? data.showOnlyToday : data.location.open;

    const availableButtomSheet = me._bottomSheetHelperService.open(AvailableServiceTimeComponent, {
      data: {
        isLocationOpen: isLocationOpen,
        locationOpenDays: data.location.openDays,
        serviceMode: data.serviceMode,
        hasValidOrderTimes: data.hasValidOrderTimes,
        hasValidTimesToday: data.hasValidOrderTimesToday
      },
      panelClass: "mat-bottom-sheet-container--full-height"
    });
    availableButtomSheet.afterDismissed().subscribe(async (result: boolean) => {
      if (navigateOnClose) {
        me.loadNavigtion(result, data);
      }
    });
  }
  // endregion
}
